/**
 * The small part of WebUSB / Web Bluetooth the printer drivers use (Chrome on Android / SUNMI /
 * desktop). Declared here instead of adding `@types/w3c-web-usb` + `@types/web-bluetooth`;
 * reached through `navigatorHardware()` so tests can inject fakes.
 */

export interface UsbEndpointLike {
  endpointNumber: number;
  direction: "in" | "out";
  type: "bulk" | "interrupt" | "isochronous";
}

export interface UsbAlternateLike {
  interfaceClass: number;
  endpoints: UsbEndpointLike[];
}

export interface UsbInterfaceLike {
  interfaceNumber: number;
  claimed: boolean;
  alternate: UsbAlternateLike;
  alternates?: UsbAlternateLike[];
}

export interface UsbConfigurationLike {
  configurationValue: number;
  interfaces: UsbInterfaceLike[];
}

export interface UsbDeviceLike {
  vendorId: number;
  productId: number;
  serialNumber?: string | null;
  productName?: string | null;
  manufacturerName?: string | null;
  opened: boolean;
  configuration: UsbConfigurationLike | null;
  configurations?: UsbConfigurationLike[];
  open(): Promise<void>;
  close(): Promise<void>;
  selectConfiguration(value: number): Promise<void>;
  claimInterface(interfaceNumber: number): Promise<void>;
  transferOut(
    endpointNumber: number,
    data: ArrayBufferView,
  ): Promise<{ status: "ok" | "stall" | "babble"; bytesWritten: number }>;
}

export interface UsbLike {
  requestDevice(options: { filters: Array<Record<string, number>> }): Promise<UsbDeviceLike>;
  getDevices(): Promise<UsbDeviceLike[]>;
  addEventListener?(
    type: "disconnect" | "connect",
    listener: (event: { device: UsbDeviceLike }) => void,
  ): void;
}

export interface GattCharacteristicLike {
  uuid: string;
  properties: { write: boolean; writeWithoutResponse: boolean };
  writeValueWithoutResponse?(value: ArrayBufferView): Promise<void>;
  writeValueWithResponse?(value: ArrayBufferView): Promise<void>;
  writeValue?(value: ArrayBufferView): Promise<void>;
}

export interface GattServiceLike {
  uuid: string;
  getCharacteristics(): Promise<GattCharacteristicLike[]>;
}

export interface GattServerLike {
  connected: boolean;
  connect(): Promise<GattServerLike>;
  disconnect(): void;
  getPrimaryServices(): Promise<GattServiceLike[]>;
}

export interface BluetoothDeviceLike {
  id: string;
  name?: string | null;
  gatt?: GattServerLike | null;
  addEventListener?(type: "gattserverdisconnected", listener: () => void): void;
}

export interface BluetoothLike {
  requestDevice(options: {
    filters?: Array<{ services?: string[]; namePrefix?: string }>;
    optionalServices?: string[];
    acceptAllDevices?: boolean;
  }): Promise<BluetoothDeviceLike>;
  getDevices?(): Promise<BluetoothDeviceLike[]>;
}

export interface NavigatorHardware {
  usb?: UsbLike;
  bluetooth?: BluetoothLike;
}

export function navigatorHardware(): NavigatorHardware {
  if (typeof navigator === "undefined") {
    return {};
  }
  return navigator as unknown as NavigatorHardware;
}
